import { useModalForm } from 'assets/components/form/modalForm/ModalForm';
import Icon from 'assets/components/icon/Icon';
import DateInput from 'assets/components/inputs/dateInput/DateInput';
import FileInputSimple from 'assets/components/inputs/file/FileInputSimple';
import NumberInput from 'assets/components/inputs/number/NumberInput';
import SelectInput from 'assets/components/inputs/select/SelectInput';
import TextInput from 'assets/components/inputs/text/TextInput';
import FormWrapper from 'assets/components/inputs/wrappers/FormWrapper';
import InputWrapper from 'assets/components/inputs/wrappers/InputWrapper';
import DocumentPreview from 'assets/components/other/DocumentPreview';
import { useDebounce } from 'assets/hooks/events/debounce/useDebounce';
import { getLocales } from 'assets/locales/Locale';
import { defaultInputDateTimeFormat } from 'assets/utils/data/Date';
import MimeType from 'assets/utils/file/mimeType/MimeType';
import { HttpStatus } from 'config/Api.Config';
import AccountReceivableEvidenceSources from 'models/enums/AccountReceivableEvidenceSource';
import PaymentType from 'models/enums/PaymentType';
import accountReceivableStatementCandidatesApi from 'models/finance/accountReceivable/statementCandidates/Api.AccountReceivable.StatementCandidates';
import accountReceivableStatementCandidatesFindApi from 'models/finance/accountReceivable/statementCandidatesForAccountReceivable/Api.AccountReceivable.StatementCandidatesForAccountReceivable';
import { useEffect } from 'react';
import {
  AccountReceivableAction,
  AccountReceivableForm,
  useAccountReceivableProvider,
} from '../../Finance.AccountReceivable.Hooks';

const FinanceAccountReceivableEditInfo = () => {
  const { lang, translate } = getLocales();
  const { form } = useModalForm<AccountReceivableForm>();
  const { dealerListApi, pageRouter } = useAccountReceivableProvider();

  const isEdit = !!form.data?.id;
  const debouncedAmount = useDebounce(form.data?.amount, 1000);

  useEffect(() => {
    if (pageRouter.params.action === AccountReceivableAction.CREATE) {
      (async () => {
        if (
          !form.data?.dealerId &&
          !debouncedAmount &&
          !form.data?.paymentReceivedAtUtc &&
          !form.data?.dealerDisplayName
        )
          return;

        const res = await accountReceivableStatementCandidatesApi({
          dealerId: form.data?.dealerId,
          dealerDisplayName: form.data?.dealerDisplayName,
          amount: debouncedAmount,
          paymentReceivedAtUtc: form.data?.paymentReceivedAtUtc,
        });

        if (res.status === HttpStatus.ok && res.payload) {
          form.update({
            statementCandidates: res.payload.map((it) => ({
              ...it,
              isSelected: false,
            })),
          });
        }
      })();
    }
  }, [
    pageRouter.params.action,
    form.data?.dealerId,
    form.data?.dealerDisplayName,
    debouncedAmount,
    form.data?.paymentReceivedAtUtc,
  ]);

  useEffect(() => {
    if (pageRouter.params.action === AccountReceivableAction.EDIT) {
      (async () => {
        const res = await accountReceivableStatementCandidatesFindApi(form.data?.id);

        if (res.status === HttpStatus.ok && res.payload) {
          form.update({
            statementCandidates: res.payload.map((it) => ({
              ...it,
              isSelected: false,
            })),
          });
        }
      })();
    }
  }, [pageRouter.params.action, form.data?.id]);

  return (
    <FormWrapper>
      {!isEdit && (
        <SelectInput
          name="dealerId"
          label={lang.dealer}
          data={dealerListApi.payload?.data?.map((item) => item.displayInfo) ?? []}
          value={form.data.dealerId}
          onChange={({ dealerId }) => {
            form.update({
              dealerId,
              dealerDisplayName: dealerListApi?.payload?.data?.find((it) => it.id === dealerId)?.name,
            });
          }}
          error={!isEdit ? form.errors.dealerId : undefined}
          icon={<Icon class="fas-store" />}
          disabled={isEdit}
        />
      )}
      <TextInput
        name="dealerDisplayName"
        label={lang.dealerName}
        value={form.data.dealerDisplayName}
        onChange={form.update}
        error={form.errors.dealerDisplayName}
        icon={<Icon class="fas-pencil" />}
        readOnly
      />
      <NumberInput
        name="amount"
        label={lang.amount}
        value={form.data.amount}
        onChange={form.update}
        error={form.errors.amount}
        icon={<Icon class="fas-dollar-sign" />}
        step={0.01}
        disabled={isEdit}
      />
      <SelectInput
        name="paymentMethodCode"
        label={lang.paymentMethod}
        error={form.errors.paymentMethodCode}
        data={Object.values(PaymentType).map((it) => ({
          id: it,
          title: translate(it),
        }))}
        value={form.data?.paymentMethodCode}
        onChange={form.update}
        disabled={isEdit}
      />
      {form.data.paymentMethodCode === PaymentType.cheque && (
        <>
          <InputWrapper>
            <TextInput
              name="chequeNumber"
              label={lang.chequeNumber}
              value={form.data.chequeNumber}
              onChange={form.update}
              error={form.errors.chequeNumber}
              icon={<Icon class="fas-money-check" />}
            />
            <DateInput
              name="chequeDateAtUtc"
              label={lang.chequeDate}
              dateTimeFormat={defaultInputDateTimeFormat}
              value={form.data?.chequeDateAtUtc}
              onChange={form.update}
              icon={<Icon class="fas-calendar-alt" />}
              error={form.errors?.chequeDateAtUtc}
            />
          </InputWrapper>
          <FileInputSimple
            name="files"
            label={lang.chequeDocument}
            value={form.data.chequeFile ? [form.data.chequeFile] : []}
            allowedMimeTypes={[MimeType.allImageFiles, MimeType.appPDF]}
            error={form.errors?.chequeFile?._objectError}
            onChange={({ files }) => {
              form.update({
                chequeFile: files[0],
              });
            }}
          />
          {form.data.chequeFile && (
            <>
              <DocumentPreview
                documents={[form.data?.chequeFile].map((it) => ({
                  id: it.id,
                  name: it.original?.name,
                  extension: it.original?.type,
                  info: lang.new,
                  documentType: it.original?.type?.startsWith('image') ? lang.image : lang.document,
                  onDelete: () => {
                    form.update({ chequeFile: undefined });
                  },
                }))}
                showGallery={true}
              />
              &nbsp;
            </>
          )}
          <TextInput
            name="externalPaymentReference"
            label={lang.externalPaymentReference}
            value={form.data.externalPaymentReference}
            onChange={form.update}
            error={form.errors.externalPaymentReference}
            icon={<Icon class="fas-link" />}
          />
        </>
      )}
      <SelectInput
        name="evidenceSource"
        label={lang.evidenceSource}
        error={form.errors.evidenceSource}
        data={Object.values(AccountReceivableEvidenceSources).map((it) => ({
          id: it,
          title: translate(it),
        }))}
        value={form.data?.evidenceSource}
        onChange={form.update}
        disabled={isEdit}
      />
      <DateInput
        name="paymentReceivedAtUtc"
        label={lang.datePaymentReceived}
        dateTimeFormat={defaultInputDateTimeFormat}
        value={form.data?.paymentReceivedAtUtc}
        onChange={form.update}
        icon={<Icon class="fas-calendar-alt" />}
        error={form.errors?.paymentReceivedAtUtc}
        disabled={isEdit}
      />
      &nbsp;
      <InputWrapper>
        <TextInput
          name="contactName"
          label={lang.contactName}
          value={form.data.contactName}
          onChange={form.update}
          error={form.errors.contactName}
          icon={<Icon class="fas-user" />}
        />
        <TextInput
          name="contactEmail"
          label={lang.contactEmail}
          value={form.data.contactEmail}
          onChange={form.update}
          error={form.errors.contactEmail}
          icon={<Icon class="fas-envelope" />}
        />
      </InputWrapper>
    </FormWrapper>
  );
};

export default FinanceAccountReceivableEditInfo;
